<template>
  <div>
    <div class="row mb-4">
      <div class="col-12">
        <Card cardClasses="border-0 shadow-sm rounded-4">
          <template v-slot:header>
            <div class="d-flex align-items-center justify-content-between p-3">
              <div class="d-flex align-items-center">
                <div class="icon-circle bg-primary text-white me-3">
                  <vue-feather type="clock" size="20"></vue-feather>
                </div>
                <div>
                  <h4 class="mb-0 fw-bold text-primary">System Logs</h4>
                  <p class="text-muted mb-0 small">Track and monitor system activities ({{logs.data.length}} entries)</p>
                </div>
              </div>
            </div>
          </template>

          <!-- Filter Section -->
          <div class="p-3">
            <div class="card border-0 shadow-sm rounded-4 mb-4">
              <div class="card-header bg-light d-flex align-items-center">
                <div class="icon-circle-sm bg-primary text-white me-2">
                  <vue-feather type="filter" size="14"></vue-feather>
                </div>
                <h5 class="mb-0 fw-bold">Filter Logs</h5>
              </div>
              <div class="card-body p-3">
                <div class="row g-3">
                  <div class="form-group col-md-2">
                    <label class="form-label fw-medium">User:</label>
                    <div class="input-group">
                      <span class="input-group-text bg-light">
                        <vue-feather type="user" size="16"></vue-feather>
                      </span>
                      <select class="form-select shadow-sm" v-model="search.causer_id" data="">
                        <option value="" selected>All users</option>
                        <option v-for="user in users" :key="user.id" :value="user.id">{{user.first_name}} {{user.last_name}}</option>
                      </select>
                    </div>
                  </div>
                  <div class="form-group col-md-2">
                    <label class="form-label fw-medium">Module:</label>
                    <div class="input-group">
                      <span class="input-group-text bg-light">
                        <vue-feather type="package" size="16"></vue-feather>
                      </span>
                      <select class="form-select shadow-sm" v-model="search.log_name" data="">
                        <option value="" selected>All modules</option>
                        <option v-for="module in modules" :key="module.id" :value="module.module">{{module.name}}</option>
                      </select>
                    </div>
                  </div>
                  <div class="form-group col-md-2">
                    <label class="form-label fw-medium">Action:</label>
                    <div class="input-group">
                      <span class="input-group-text bg-light">
                        <vue-feather type="activity" size="16"></vue-feather>
                      </span>
                      <select class="form-select shadow-sm" v-model="search.event" data="">
                        <option value="" selected>All actions</option>
                        <option value="created">Created</option>
                        <option value="updated">Updated</option>
                        <option value="deleted">Deleted</option>
                        <option value="login">Login</option>
                        <option value="logout">Logout</option>
                      </select>
                    </div>
                  </div>
                  <div class="form-group col-md-2">
                    <label class="form-label fw-medium">From Date:</label>
                    <div class="input-group">
                      <span class="input-group-text bg-light">
                        <vue-feather type="calendar" size="16"></vue-feather>
                      </span>
                      <input type="date" class="form-control shadow-sm" v-model="search.from">
                    </div>
                  </div>
                  <div class="form-group col-md-2">
                    <label class="form-label fw-medium">To Date:</label>
                    <div class="input-group">
                      <span class="input-group-text bg-light">
                        <vue-feather type="calendar" size="16"></vue-feather>
                      </span>
                      <input type="date" class="form-control shadow-sm" v-model="search.to">
                    </div>
                  </div>
                  <div class="form-group col-md-2 d-flex align-items-end">
                    <button class="btn btn-warning rounded-pill w-100" @click="resetFilter">
                      <vue-feather type="refresh-cw" size="16" class="me-1"></vue-feather>
                      Reset Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-5">
            <Spinner spinner-class="mb-1" />
            <p class="text-muted">Loading log data...</p>
          </div>

          <!-- Logs Table -->
          <div v-else class="px-3">
            <div class="card border-0 shadow-sm rounded-4">
              <div class="card-header bg-light d-flex align-items-center">
                <div class="icon-circle-sm bg-primary text-white me-2">
                  <vue-feather type="list" size="14"></vue-feather>
                </div>
                <h5 class="mb-0 fw-bold">Activity Logs</h5>
              </div>
              <div class="card-body p-0">
                <div class="table-responsive">
                  <table class="table table-hover mb-0">
                    <thead class="table-light">
                      <tr>
                        <th scope="col">User</th>
                        <th scope="col">Module</th>
                        <th scope="col">Action</th>
                        <th scope="col">Description</th>
                        <th scope="col">Date</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="log in logs.data" :key="log.id">
                        <td>{{ log.description }}</td>
                        <td>
                          <Badge pill type="primary" class="badge-sm">
                            {{ log.log_name }}
                          </Badge>
                        </td>
                        <td>
                          <Badge pill :type="getActionType(log.event)" class="badge-sm">
                            <vue-feather :type="getActionIcon(log.event)" size="12" class="me-1"></vue-feather>
                            {{ log.event }}
                          </Badge>
                        </td>
                        <td>{{ log.event }} {{ log.log_name }}</td>
                        <td>{{ formatDateTime(log.created_at) }}</td>
                        <td>
                          <button
                              class="btn btn-primary btn-sm rounded-pill"
                              @click="logModal = true; populateLog(log)"
                          >
                            <vue-feather type="eye" size="14" class="me-1"></vue-feather>
                            View
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination Controls -->
          <div class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-3 justify-content-between border-top mt-3">
            <button class="btn btn-outline-dark rounded-pill" @click="$router.go(-1)">
              <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
              Back
            </button>
            <div class="me-sm-4 mb-sm-0 mb-3">
              <p class="mb-2 small">Rows per page</p>
              <select class="form-select shadow-sm" v-model="search.per_page">
                <option value="10">10</option>
                <option value="15">15</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            <div>
              <Pagination :pages="logs.last_page" v-model="search.page">
                <template v-slot:next>
                  <vue-feather type="chevron-right" size="16"></vue-feather>
                </template>
                <template v-slot:previous>
                  <vue-feather type="chevron-left" size="16"></vue-feather>
                </template>
              </Pagination>
            </div>
          </div>
        </Card>
      </div>
    </div>

    <!-- Log Details Modal -->
    <Modal :open="logModal" dialog-size="modal-lg" @closed="logModal = false">
      <div class="modal-header bg-light">
        <div class="d-flex align-items-center">
          <div class="icon-circle-sm bg-primary text-white me-2">
            <vue-feather type="info" size="16"></vue-feather>
          </div>
          <h5 class="modal-title m-0 fw-bold">Log Details</h5>
        </div>
        <button @click="logModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body p-4">
        <div class="row g-4">
          <div class="col-md-6">
            <div class="info-card p-3 bg-light rounded-4">
              <h6 class="mb-2 fw-bold d-flex align-items-center">
                <vue-feather type="user" size="14" class="me-2 text-primary"></vue-feather>
                User
              </h6>
              <p class="mb-0 fw-medium">{{ log.description }}</p>
            </div>
          </div>
          <div class="col-md-6">
            <div class="info-card p-3 bg-light rounded-4">
              <h6 class="mb-2 fw-bold d-flex align-items-center">
                <vue-feather type="calendar" size="14" class="me-2 text-primary"></vue-feather>
                Date & Time
              </h6>
              <p class="mb-0 fw-medium">{{ formatDateTime(log.created_at) }}</p>
            </div>
          </div>
          <div class="col-md-12">
            <div class="info-card p-3 bg-light rounded-4">
              <h6 class="mb-2 fw-bold d-flex align-items-center">
                <vue-feather type="file-text" size="14" class="me-2 text-primary"></vue-feather>
                Description
              </h6>
              <p class="mb-0 fw-medium">{{ log.event + ' ' + log.log_name }}</p>
            </div>
          </div>
          <div class="col-md-12">
            <div class="info-card p-3 bg-light rounded-4">
              <h6 class="mb-2 fw-bold d-flex align-items-center">
                <vue-feather type="database" size="14" class="me-2 text-primary"></vue-feather>
                Data
              </h6>
              <pre class="mb-0 bg-white p-3 rounded-3 border" style="max-height: 300px; overflow-y: auto;">{{ formatLogProperties(log.properties) }}</pre>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary rounded-pill" data-dismiss="modal" @click="logModal = false">
          <vue-feather type="x" size="16" class="me-1"></vue-feather>
          Close
        </button>
      </div>
    </Modal>
    <!-- Log Details Modal End -->
  </div>
</template>

<script>

import settingService from "@/api/settingService";
import userService from "@/api/userService";
import {encrypt, decrypt} from "@/utils/crypto";
import VueFeather from 'vue-feather';

export default {
  name: "Logs",
  components: {
    VueFeather
  },

  data() {
    return {
      loading: true,
      logs: {
        data: []
      },
      log: {},
      users: [],
      modules: [],
      logModal: false,
      search: {
        page: 1,
        per_page: 10,
        log_name: '',
        causer_id: '',
        event: '',
        from: '',
        to: '',
      },
    }
  },

  methods: {
    formatDateTime(dateTimeStr) {
      return dateTimeStr.replace("T", ' ').replace(".000000Z", '');
    },
    formatLogProperties(properties) {
      if (!properties) return 'No data';

      try {
        if (typeof properties === 'string') {
          return JSON.stringify(JSON.parse(properties), null, 2);
        } else {
          return JSON.stringify(properties, null, 2);
        }
      } catch (e) {
        return properties.toString();
      }
    },
    getActionType(action) {
      switch(action.toLowerCase()) {
        case 'created':
          return 'success';
        case 'updated':
          return 'info';
        case 'deleted':
          return 'danger';
        case 'login':
          return 'primary';
        case 'logout':
          return 'secondary';
        default:
          return 'primary';
      }
    },
    getActionIcon(action) {
      switch(action.toLowerCase()) {
        case 'created':
          return 'plus-circle';
        case 'updated':
          return 'edit';
        case 'deleted':
          return 'trash-2';
        case 'login':
          return 'log-in';
        case 'logout':
          return 'log-out';
        default:
          return 'activity';
      }
    },
    populateLog(log) {
      this.log = log
    },
    async resetFilter() {
      window.location.reload()
    },
    async getLogs() {
      this.loading = true
      this.logs = await settingService.getLogs(this.search)
      this.loading = false
    },
    async getUsersAndModules() {
      if (sessionStorage.getItem('users')) {
        this.modules = JSON.parse(decrypt(sessionStorage.getItem('modules')))
        this.users = JSON.parse(decrypt(sessionStorage.getItem('users')))
      } else {
        this.users = await userService.getUsers()
        this.modules = await settingService.getModules()
        sessionStorage.setItem('users', encrypt(JSON.stringify(this.users)))
        sessionStorage.setItem('modules', encrypt(JSON.stringify(this.modules)))
      }
    }
  },

  async created() {
    await this.getLogs()
    await this.getUsersAndModules()
  },

  watch: {
    search: {
      async handler(val) {
        await this.getLogs()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
/* Icon styles */
.icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-circle-sm {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Card styles */
.info-card {
  transition: all 0.3s ease;
  border: 1px solid rgba(0,0,0,0.05);
}

.info-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

/* Badge styles */
.badge-lg {
  font-size: 0.85rem;
  padding: 0.35rem 0.65rem;
}

.badge-sm {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

/* Table styles */
.table th {
  font-weight: 600;
  white-space: nowrap;
}

.table td {
  vertical-align: middle;
}

/* Button hover effects */
.btn-primary.rounded-pill:hover,
.btn-warning.rounded-pill:hover,
.btn-outline-dark.rounded-pill:hover,
.btn-outline-secondary.rounded-pill:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .form-group {
    margin-bottom: 1rem;
  }

  .table-responsive {
    font-size: 0.9rem;
  }
}
</style>
